import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useExplorerStore } from "../../store/explorerStore";
import { useFileExplorer } from "../../hooks/useFileExplorer";
import { useSearch } from "../../hooks/useSearch";
import { FileToolbar } from "./FileToolbar";
import { VirtualFileList } from "./VirtualFileList";
import { VirtualFileGrid } from "./VirtualFileGrid";
import { SelectionBar } from "./SelectionBar";
import { MoveToFolderModal } from "./MoveToFolderModal";
import { DeleteConfirmModal } from "../ui/DeleteConfirmModal";
import { PreviewModal } from "../preview/PreviewModal";
import { Spinner } from "../ui/Spinner";
import { FileListSkeleton } from "../ui/Skeleton";
import { moveItems } from "../../api/move.api";
import { toast } from "sonner";
import { ApiError } from "../../api/client";
import type { ExplorerItem, FileItem } from "@titancloud/shared";

function StatusBar({
  loadedFilesCount,
  totalFilesCount,
  isFetchingNextPage,
}: {
  loadedFilesCount: number;
  totalFilesCount: number;
  isFetchingNextPage: boolean;
}) {
  return (
    <div className="flex-shrink-0 border-t border-gray-800 bg-gray-950/60 px-4 py-2.5 flex items-center justify-between gap-4">
      <span className="text-xs text-gray-500">
        Showing{" "}
        <span className="text-gray-300 font-medium">{loadedFilesCount}</span>
        {" "}of{" "}
        <span className="text-gray-300 font-medium">{totalFilesCount}</span>
        {" "}file{totalFilesCount !== 1 ? "s" : ""}
      </span>
      {isFetchingNextPage && (
        <span className="flex items-center gap-2 text-xs text-gray-500">
          <Spinner size="sm" />
          Loading more…
        </span>
      )}
    </div>
  );
}

function formatMoveError(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === "MOVE_CONFLICT" && e.conflicts?.length) {
      return e.conflicts.map((c) => c.reason).join("\n");
    }
    return e.message;
  }
  return "Move failed";
}

interface PendingDelete {
  items: Array<{ id: string; kind: "file" | "folder"; name: string }>;
}

export default function FileExplorer() {
  const { folderId } = useParams<{ folderId?: string }>();
  const { setCurrentFolder, selectedIds, clearSelection, selectionMode, exitSelectionMode, searchQuery, viewMode } = useExplorerStore();
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveError, setMoveError] = useState<string | null>(null);
  const qc = useQueryClient();

  const search = useSearch(searchQuery);
  const explorer = useFileExplorer();

  const moveMutation = useMutation({
    mutationFn: moveItems,
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ["folders"] });
      void qc.invalidateQueries({ queryKey: ["files"] });
      void qc.invalidateQueries({ queryKey: ["search"] });
      clearSelection();
      exitSelectionMode();
      setMoveOpen(false);
      setMoveError(null);
      const count = vars.items.length;
      toast.success(`Moved ${count} item${count !== 1 ? "s" : ""}`);
    },
    onError: (e: unknown) => {
      setMoveError(formatMoveError(e));
    },
  });

  useEffect(() => {
    setCurrentFolder(folderId ?? null);
  }, [folderId, setCurrentFolder]);

  const items: ExplorerItem[] = searchQuery.length >= 2
    ? (search.data?.files ?? []).map((f) => ({ ...f, kind: "file" as const }))
    : explorer.allItems;

  const isLoading = searchQuery.length >= 2 ? search.isLoading : explorer.isLoading;

  const totalCount = searchQuery.length >= 2
    ? (search.data?.files.length ?? 0)
    : explorer.totalCount;

  const isSearching = searchQuery.length >= 2;

  // Search results aren't paginated, so infinite-scroll only applies to normal folder browsing.
  const paginationProps = isSearching
    ? {}
    : {
        hasNextPage: explorer.hasNextPage,
        isFetchingNextPage: explorer.isFetchingNextPage,
        onLoadMore: () => void explorer.fetchNextPage(),
      };

  const selectedItems = useMemo(
    () => items.filter((i) => selectedIds.has(i.id)),
    [items, selectedIds]
  );

  const fileItems = items.filter((i): i is FileItem => i.kind === "file");
  const openPreview = (fileId: string) => {
    const idx = fileItems.findIndex((f) => f.id === fileId);
    if (idx !== -1) setPreviewIndex(idx);
  };

  const requestDelete = (toDelete: Array<{ id: string; kind: "file" | "folder"; name: string }>) => {
    if (toDelete.length === 0) return;
    setPendingDelete({ items: toDelete });
  };

  const confirmDelete = () => {
    if (!pendingDelete) return;
    const { items } = pendingDelete;
    for (const item of items) {
      explorer.delete({ id: item.id, kind: item.kind });
    }
    if (items.length > 1) {
      toast.success(`${items.length} items moved to trash`);
    }
    clearSelection();
    setPendingDelete(null);
  };

  const handleSelectionDelete = () => {
    const toDelete = Array.from(selectedIds).flatMap((id) => {
      const item = items.find((i) => i.id === id);
      return item ? [{ id, kind: item.kind, name: item.name }] : [];
    });
    requestDelete(toDelete);
  };

  return (
    <div className="flex h-full flex-col">
      <FileToolbar
        onCreateFolder={(name) => explorer.createFolder({ name })}
        isCreatingFolder={explorer.isCreatingFolder}
      />

      {(selectionMode || selectedIds.size > 0) && (
        <SelectionBar
          count={selectedIds.size}
          onDelete={handleSelectionDelete}
          onMove={() => {
            setMoveError(null);
            setMoveOpen(true);
          }}
          onExit={exitSelectionMode}
        />
      )}

      {isLoading ? (
        <FileListSkeleton />
      ) : items.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 text-gray-500">
          <svg className="h-12 w-12" viewBox="0 0 48 48" fill="none">
            <path d="M8 12a4 4 0 014-4h10l4 4h14a4 4 0 014 4v20a4 4 0 01-4 4H12a4 4 0 01-4-4V12z" stroke="currentColor" strokeWidth="2" />
          </svg>
          <p className="text-sm">
            {searchQuery ? "No files match your search" : "This folder is empty"}
          </p>
          <p className="text-xs">Drop files here to upload</p>
        </div>
      ) : viewMode === "grid" ? (
        <VirtualFileGrid
          items={items}
          onOpen={openPreview}
          onRename={(id, kind, name) => explorer.rename({ id, kind, name })}
          onDelete={(id, kind, name) => requestDelete([{ id, kind, name }])}
          isCreatingFolder={explorer.isCreatingFolder}
          creatingFolderName={explorer.creatingFolderName}
          {...paginationProps}
        />
      ) : (
        <VirtualFileList
          items={items}
          onRename={(id, kind, name) => explorer.rename({ id, kind, name })}
          onDelete={(id, kind, name) => requestDelete([{ id, kind, name }])}
          isCreatingFolder={explorer.isCreatingFolder}
          creatingFolderName={explorer.creatingFolderName}
          {...paginationProps}
        />
      )}

      {previewIndex !== null && fileItems.length > 0 && (
        <PreviewModal
          files={fileItems}
          initialIndex={previewIndex}
          onClose={() => setPreviewIndex(null)}
          {...(isSearching
            ? {}
            : {
                hasMore: explorer.hasNextPage,
                isLoadingMore: explorer.isFetchingNextPage,
                onLoadMore: () => void explorer.fetchNextPage(),
              })}
        />
      )}

      {pendingDelete && (
        <DeleteConfirmModal
          count={pendingDelete.items.length}
          {...(pendingDelete.items.length === 1 ? { itemName: pendingDelete.items[0]!.name } : {})}
          onConfirm={confirmDelete}
          onCancel={() => setPendingDelete(null)}
        />
      )}

      <MoveToFolderModal
        open={moveOpen}
        onClose={() => {
          setMoveOpen(false);
          setMoveError(null);
        }}
        selectedItems={selectedItems}
        isPending={moveMutation.isPending}
        errorMessage={moveError}
        onMove={(targetFolderId) => {
          setMoveError(null);
          moveMutation.mutate({
            targetFolderId,
            items: selectedItems.map((i) => ({
              id: i.id,
              kind: i.kind,
            })),
          });
        }}
      />

      {/* Status bar — pagination itself is driven by scroll position inside the grid/list */}
      {!isLoading && !isSearching && explorer.isLoadingFiles && (
        <div className="flex-shrink-0 border-t border-gray-800 bg-gray-950/60 px-4 py-2.5 flex items-center gap-2 text-xs text-gray-500">
          <Spinner size="sm" />
          Loading files…
        </div>
      )}
      {!isLoading && !isSearching && explorer.totalFilesCount > 0 && (
        <StatusBar
          loadedFilesCount={explorer.loadedFilesCount}
          totalFilesCount={explorer.totalFilesCount}
          isFetchingNextPage={explorer.isFetchingNextPage}
        />
      )}
      {!isLoading && isSearching && totalCount > 0 && (
        <div className="flex-shrink-0 border-t border-gray-800 py-1.5 text-center text-xs text-gray-500">
          {totalCount} result{totalCount !== 1 ? "s" : ""}
        </div>
      )}
    </div>
  );
}
